import { Injectable } from '@angular/core';
import {
  AccountSummary,
  ApiRestService,
  RedeemedReward,
  ReferenceData,
  SearchResult,
  BaseUser,
} from '@motivforce/mx-library-angular';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';

import { AwardSalesRequest } from '../../model/core/award-sales-request';
import { AwardSalesSearch } from '../../model/core/award-sales-search';
import { BusinessRuleMessage } from '../../model/core/business-rule-message';
import { CommunicationsPreference } from '../../model/core/communications-preference';
import { CompanyClaimedInvoiceResult } from '../../model/core/company-claimed-invoice-result';
import { Currency } from '../../model/core/currency';
import { InvoiceSearch } from '../../model/core/invoice-search';
import { MemberEngagementPage } from '../../model/core/member-engagement-page';
import { MemberEngagementSearch } from '../../model/core/member-engagement-search';
import { Profile } from '../../model/core/profile';
import { RedemptionSearch } from '../../model/core/redemption-search';
import { RewardCollegue } from '../../model/core/reward-colleague';
import { SetPassword } from '../../model/core/set-password';
import { TaxRequirements } from '../../model/core/tax-requirements';
import { Transaction } from '../../model/core/transaction';
import { TransactionSearch } from '../../model/core/transaction-search';
import { TransferPoints } from '../../model/core/transfer-points';
import { TransferTransactionSearch } from '../../model/core/transfer-transaction-search';
import { LeapPerformanceSummary } from '../../model/leap-performance-summary';
import { LenovoLeapProfile } from '../../model/lenovo/lenovo-leap-profile';
import { LenovoLeapSalaryBand } from '../../model/lenovo/lenovo-leap-salary-band';
import { CustomerMeeting } from '../../model/lenovo/submissions/customer-meeting';
import { PromotionTransaction } from '../../model/promotion/promotion-transaction';
import { PromotionTransactionSearch } from '../../model/promotion/promotion-transaction-search';
import { PromotionGenericTransactionSearch } from '../../model/promotion/promotion-transaction-search-generic';
import { SpinToWin } from '../../model/promotion/spin-to-win';
import { User } from '../../model/user';

@Injectable({
  providedIn: 'root',
})
export class UserRestService {
  constructor(private apiRest: ApiRestService) {}

  getAccountSummary(): Observable<AccountSummary> {
    return this.apiRest.get<AccountSummary>(`${environment.api.core.baseUrl}/members/users/account-summary`);
  }

  getYearAccountSummaries(): Observable<AccountSummary[]> {
    return this.apiRest.get<AccountSummary[]>(`${environment.api.core.baseUrl}/members/users/points/year-summaries`);
  }

  getLeapPerformanceSummary(): Observable<LeapPerformanceSummary> {
    return this.apiRest.get<LeapPerformanceSummary>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/leap-performance`
    );
  }

  getProfile(): Observable<Profile> {
    return this.apiRest.get<Profile>(`${environment.api.core.baseUrl}/members/users/profile`);
  }

  getLenovoLeapProfile(): Observable<LenovoLeapProfile> {
    return this.apiRest.get<LenovoLeapProfile>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/profile`
    );
  }

  updateLenovoLeapProfile(leapProfile: LenovoLeapProfile): Observable<LenovoLeapProfile> {
    return this.apiRest.put<LenovoLeapProfile>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/profile`,
      leapProfile
    );
  }

  submitLenovoLeapPresignedUrl(fileName: string, fileContentType: string): Observable<any> {
    return this.apiRest.post<any>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/profile/presigned-url`,
      {
        fileName,
        fileContentType,
      }
    );
  }

  searchTransactions(searchCriteria: TransactionSearch): Observable<SearchResult<Transaction>> {
    return this.apiRest.post<SearchResult<Transaction>>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/account-statement`,
      searchCriteria
    );
  }

  getTransactionTypes(): Observable<ReferenceData[]> {
    return this.apiRest.get<ReferenceData[]>(`${environment.api.core.baseUrl}/members/transactions/types`);
  }

  searchPromotions(searchCriteria: TransactionSearch): Observable<SearchResult<Transaction>> {
    return this.apiRest.post<SearchResult<Transaction>>(
      `${environment.api.core.baseUrl}/members/users/promotion-transactions`,
      searchCriteria
    );
  }

  searchRedeemedRewards(searchCriteria: RedemptionSearch): Observable<SearchResult<RedeemedReward>> {
    return this.apiRest.post<SearchResult<RedeemedReward>>(
      `${environment.api.core.baseUrl}/members/redemptions/search`,
      searchCriteria
    );
  }

  getCommunicationPreferences(): Observable<any> {
    return this.apiRest.get(
      `${environment.api.core.baseUrl}/members/clients/lenovoleap/profile/communications-preferences`
    );
  }

  updateCommunicationPreferences(form: CommunicationsPreference): Observable<any> {
    return this.apiRest.post(`${environment.api.core.baseUrl}/members/users/profile/send-email`, form);
  }

  acceptTermsConditions(): Observable<any> {
    return this.apiRest.post(`${environment.api.core.baseUrl}/members/users/terms-and-conditions/accept`, null);
  }

  updateVenue(venueId: number): Observable<void> {
    return this.apiRest.put<void>(`${environment.api.core.baseUrl}/members/clients/amber/userdetails/venue`, {
      venueId,
    });
  }

  setPassword(password: SetPassword) {
    return this.apiRest.post(`${environment.api.core.baseUrl}/members/users/set-password`, password);
  }

  createCheckoutSession(form: any): Observable<any> {
    return this.apiRest.post(`${environment.api.core.baseUrl}/members/payments/create-checkout-session`, form);
  }

  rewardColleague(reward: RewardCollegue): Observable<any> {
    return this.apiRest.post(`${environment.api.core.baseUrl}/members/clients/lenovo/users/reward-colleague`, reward);
  }

  getPrcBalance(): Observable<any> {
    return this.apiRest.get(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/balance`);
  }

  getCreditsBalance(): Observable<any> {
    return this.apiRest.get(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/balance?isCredits=true`
    );
  }

  getCompanyUsers(): Observable<BaseUser[]> {
    return this.apiRest.get<BaseUser[]>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/users/all`
    );
  }

  getActiveCompanyUsers(): Observable<BaseUser[]> {
    return this.apiRest.get<BaseUser[]>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/users/active`
    );
  }

  transferPoints(form: TransferPoints): Observable<any> {
    return this.apiRest.post(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/transfers`, form);
  }

  searchTransfers(search: TransferTransactionSearch): Observable<SearchResult<Transaction>> {
    return this.apiRest.post<SearchResult<Transaction>>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/transfers/search`,
      search
    );
  }

  searchInvoices(search: InvoiceSearch): Observable<SearchResult<CompanyClaimedInvoiceResult>> {
    return this.apiRest.post<SearchResult<CompanyClaimedInvoiceResult>>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/invoices/search`,
      search
    );
  }

  searchInvoicesToAward(search: AwardSalesSearch): Observable<SearchResult<CompanyClaimedInvoiceResult>> {
    return this.apiRest.post<SearchResult<CompanyClaimedInvoiceResult>>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/sales/search/`,
      search
    );
  }

  downloadInvoices(
    search: InvoiceSearch
  ): Observable<{ content: Blob | null; fileName: string | null; contentType: string | null }> {
    const filteredSearch = Object.fromEntries(Object.entries(search).filter(([, v]) => v != null)) as any;
    const params = new URLSearchParams(filteredSearch).toString();
    return this.apiRest
      .download(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/invoices/download?${params}`)
      .pipe(
        map((response) => ({
          content: response.body,
          fileName: response.headers.get('Filename'),
          contentType: response.headers.get('Content-Type'),
        }))
      );
  }

  searchMemberEngagement(search: MemberEngagementSearch): Observable<MemberEngagementPage> {
    return this.apiRest.post<MemberEngagementPage>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/engagement/search`,
      search
    );
  }

  downloadMemberEngagement(search: MemberEngagementSearch): Observable<{
    content: Blob | null;
    fileName: string | null;
    contentType: string | null;
  }> {
    const filteredSearch = Object.fromEntries(Object.entries(search).filter(([, v]) => v != null)) as any;
    const params = new URLSearchParams(filteredSearch).toString();
    return this.apiRest
      .download(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/engagement/download?${params}`)
      .pipe(
        map((response) => ({
          content: response.body,
          fileName: response.headers.get('Filename'),
          contentType: response.headers.get('Content-Type'),
        }))
      );
  }

  searchAvailableEmails(email: string): Observable<ReferenceData[]> {
    return this.apiRest.post<ReferenceData[]>(`${environment.api.core.baseUrl}/members/users/company-users/search`, {
      email,
    });
  }

  getBusinessRules(): Observable<BusinessRuleMessage[]> {
    return this.apiRest.get(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/business-rules`);
  }

  getTaxRequirements(): Observable<TaxRequirements> {
    return this.apiRest.get(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/tax-requirements`);
  }

  getSalaryBands(): Observable<LenovoLeapSalaryBand[]> {
    return this.apiRest.get(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/profile/dmr-salary-bands`
    );
  }

  getDmrCountries(): Observable<string[]> {
    return this.apiRest.get(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/profile/dmr-countries`);
  }

  getSpinToWin(): Observable<SpinToWin> {
    return this.apiRest.get<SpinToWin>(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/games/spin-to-win`);
  }

  getOpportunityTypes(): Observable<string[]> {
    return this.apiRest.get<string[]>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/customer-reference/opportunity-types`
    );
  }

  createCustomerMeeting(customerMeeting: CustomerMeeting): Observable<any> {
    return this.apiRest.post(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/customer-reference`,
      customerMeeting
    );
  }

  searchPromotionTransactions(
    searchCriteria: PromotionTransactionSearch
  ): Observable<SearchResult<PromotionTransaction>> {
    return this.apiRest.post<SearchResult<PromotionTransaction>>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/credits/search`,
      searchCriteria
    );
  }

  searchGenericPromotionTransactions(
    searchCriteria: PromotionGenericTransactionSearch
  ): Observable<SearchResult<PromotionTransaction>> {
    return this.apiRest.post<SearchResult<PromotionTransaction>>(
      `${environment.api.core.baseUrl}/members/promotions/${searchCriteria.promotionId}/transactions`,
      searchCriteria
    );
  }

  unsubscribeEmail(entityId: string): Observable<void> {
    return this.apiRest.post(`${environment.api.core.baseUrl}/members/support/unsubscribe?entityId=${entityId}`, null);
  }

  getEmailTemplate(entityId: string): Observable<string> {
    return this.apiRest.get(`${environment.api.core.baseUrl}/members/support/email/view?entityId=${entityId}`);
  }

  getActiveNonPrcUsers(): Observable<User[]> {
    return this.apiRest.get(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/non-prc-users/active`
    );
  }

  awardInvoice(awardInvoiceRequest: AwardSalesRequest) {
    return this.apiRest.post(`${environment.api.core.baseUrl}/members/clients/lenovo/sales/claim`, awardInvoiceRequest);
  }

  getCurrency(): Observable<Currency> {
    return this.apiRest.get<Currency>(`${environment.api.core.baseUrl}/members/users/currency`);
  }

  getCompanyAdmins(): Observable<User[]> {
    return this.apiRest.get<User[]>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/admin-users/active`
    );
  }

  searchManualTransactions(searchCriteria: TransactionSearch): Observable<SearchResult<Transaction>> {
    return this.apiRest.post<SearchResult<Transaction>>(
      `${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/transactions/search`,
      searchCriteria
    );
  }

  downloadManualTransactions(
    search: TransactionSearch
  ): Observable<{ content: Blob | null; fileName: string | null; contentType: string | null }> {
    const filteredSearch = Object.fromEntries(Object.entries(search).filter(([, v]) => v != null)) as any;
    const params = new URLSearchParams(filteredSearch).toString();
    return this.apiRest
      .download(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/companies/transactions/download?${params}`)
      .pipe(
        map((response) => ({
          content: response.body,
          fileName: response.headers.get('Filename'),
          contentType: response.headers.get('Content-Type'),
        }))
      );
  }

  updateJobTitle(jobTitle: string): Observable<void> {
    return this.apiRest.put(`${environment.api.core.baseUrl}/members/clients/lenovo/leap/users/jobTitle`, { jobTitle });
  }
}
